import { User } from "./admin";
import axios from "axios";
import { baseClient, baseUrl, getToken } from "./baseRequest";
import { Klydo } from "./klydo";
import { Plan } from "./plan";

export enum CollectionReviewStates {
  Draft = "draft",
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected",
}

export type CollectionReviewStatus = {
  state: CollectionReviewStates;
  message: string;
  /** The artist's reply to `message`, stored when they resubmit. */
  comment?: string;
};

import { MarketplaceStatus } from "./marketplace";
import type { ApprovableMarketplaceStatus } from "./marketplace";

export { MarketplaceStatus };
export type { ApprovableMarketplaceStatus };

export type DateValue = { year: number; month: number; day: number };
export type MarketplaceInfo = {
  status: MarketplaceStatus;
  // The date it was scheduled to publish on.
  date?: DateValue;
  // When it actually went live.
  publishedAt?: Date;
};

export { LabelType } from "./label";

export type SearchOptions = {
  query: string;
  skip?: number;
  limit?: number;
  filter?: Record<string, unknown>;
  returnCreator?: boolean;
  sort?: Record<string, 1 | -1>;
  populateOrder?: boolean;
};

export type ProductPageMetadata = {
  logoTypeUrl?: string;
  thumbnailBackgroundUrl?: string;
  badgeColorHex?: string;
  labels?: string[];
  eta?: DateValue;
  shopifyProductId?: string;
  affiliateBundleShopifyProductId?: string;
  affiliateBundleCreator?: "artist" | "backoffice";
};

export type PaymentDetails = {
  isAdvancedActive: boolean;
  planId?: string;
};

// The only fields that may be changed on an already-approved collection.
// `labels` maps to `productPageMetadata.labels`.
export type EditApprovedCollectionInput = {
  name?: string;
  description?: string;
  labels?: string[];
  planId?: string;
  coverUrl?: string;
  splashBackgroundUrl?: string;
  menuLogoUrl?: string;
};

export type Collection = {
  id: string;
  name: string;
  // Mongoose discriminator key returned by the API; the DraftCollection /
  // ApprovedCollection subtypes narrow it to a specific CollectionType.
  type?: CollectionType;
  coverUrl?: string;
  logoUrl?: string;
  // Themed-collection assets. Optional on the type; the backoffice requires them
  // only when a collection is marked themed. splashBackgroundUrl fills the whole
  // inner circle of the clock's themed menu page; menuLogoUrl is the themed
  // menu's header logo.
  splashBackgroundUrl?: string;
  menuLogoUrl?: string;
  klydoIds?: string[];
  klydos?: Klydo[];
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
  creatorId?: string;
  reviewStatus: CollectionReviewStatus;
  holdPendulumLogo: boolean;
  productPageMetadata?: ProductPageMetadata;
  marketplace?: MarketplaceInfo;
  paymentDetails?: PaymentDetails | null;
  // TK-634: given away for free via a generated multi-use code instead of
  // being sold — mutually exclusive with the normal paid flow.
  sponsored?: boolean;
};

export type PopulatedCollection = Collection & {
  creator: User;
  plan?: Plan;
};

export enum CollectionType {
  DRAFT = "DraftCollection",
  APPROVED = "ApprovedCollection",
}

export type DraftCollection = Collection & {
  type: CollectionType.DRAFT;
};

export type ApprovedCollection = Collection & {
  type: CollectionType.APPROVED;
};

export type PopulatedDraftCollection = DraftCollection & {
  creator: User;
};

export type PopulatedApprovedCollection = ApprovedCollection & {
  creator: User;
  plan?: Plan;
  totalPurchases?: number;
};

export type CollectionCode = {
  id: string;
  collectionId: string;
  // Resolved by the codes search aggregation from `collectionId`.
  collectionName?: string;
  orderName: string;
  code: string;
  createdAt: Date;
  clockId?: string;
  createdBy?: string;
  usedAt?: Date[];
  customerEmail?: string;
  customerName?: string;
  orderStatus?: string;
  orderCreationDate?: Date;
  orderCollections?: CollectionOrderItem[];
  collectionEmailSent?: boolean;
  // When true, the code can be redeemed by any number of clocks instead of
  // locking to the first clock that redeems it.
  multiUse?: boolean;
  // Per-clock redemption log, populated for multi-use codes.
  redemptions?: { clockId: string; redeemedAt: Date }[];
  // When set, the code can no longer be redeemed after this date.
  expiresAt?: Date;
};

export type CollectionOrderItem = {
  collectionId: string;
  code: string;
  emailSent: boolean;
};

import { Label } from "./label";
export type CollectionLabel = Label;

export type CollectionOrder = {
  orderName: string;
  collectionEmailSent?: boolean;
  status: string;
  orderCreationDate: Date;
  customerEmail: string;
  customerName: string;
  collections: CollectionOrderItem[];
};

export type PendingCollectionsResult = {
  published: number;
  skipped: number;
  failed: number;
};

export type ApproveCollectionParams = {
  collectionId: string;
  message: string;
  planId?: string;
  marketplaceStatus: ApprovableMarketplaceStatus;
};

export type CollectionApiClient = {
  upsertApprovedCollection: (
    collectionId: string,
    collection: Partial<ApprovedCollection>,
  ) => Promise<void>;
  upsertDraftCollection: (
    collectionId: string,
    collection: Partial<DraftCollection>,
  ) => Promise<void>;
  deleteCollection: (collectionId: string) => Promise<void>;
  getCollectionById: (collectionId: string) => Promise<PopulatedCollection>;
  getApprovedCollections: ({
    query,
    skip,
    limit,
    filter,
    sort,
  }: SearchOptions) => Promise<PopulatedApprovedCollection[]>;
  getReviewedCollections: (
    query: string,
    skip: number,
    limit: number,
    returnCreator?: boolean,
  ) => Promise<PopulatedDraftCollection[]>;
  getDraftCollections: ({
    query,
    skip,
    limit,
    filter,
    returnCreator,
  }: SearchOptions) => Promise<PopulatedDraftCollection[]>;
  approveCollection: ({
    collectionId,
    message,
    planId,
    marketplaceStatus,
  }: ApproveCollectionParams) => Promise<void>;
  publishCollectionToShopify: (
    collectionId: string,
    date?: DateValue,
  ) => Promise<void>;
  // Idempotent on the API side: when the collection already has an edition of
  // the given creator kind, the existing bundle's { productId, url } comes
  // back without creating anything.
  createEdition: (
    collectionId: string,
    creator: "artist" | "backoffice",
  ) => Promise<{ productId: string; url: string }>;
  publishPendingCollections: () => Promise<PendingCollectionsResult>;
  updateCollectionPlan: (collectionId: string, planId: string) => Promise<void>;
  editApprovedCollection: (
    collectionId: string,
    edit: EditApprovedCollectionInput,
  ) => Promise<void>;
  setCollectionEta: (collectionId: string, eta?: DateValue) => Promise<void>;
  downloadCollectionAssets: (
    collectionId: string,
  ) => Promise<{ blob: Blob; fileName: string }>;
  getCollectionCodes: ({
    query,
    skip,
    limit,
    sort,
    filter,
  }: Partial<SearchOptions>) => Promise<CollectionCode[]>;
  getCollectionOrders: ({
    query,
    skip,
    limit,
    sort,
  }: Omit<SearchOptions, "filter">) => Promise<CollectionOrder[]>;
  getCollectionLabels: ({
    query,
    skip,
    limit,
    sort,
  }: Omit<SearchOptions, "filter">) => Promise<CollectionLabel[]>;
  updateLabel: (labelId: string, text: string) => Promise<CollectionLabel>;
  deleteLabel: (labelId: string) => Promise<void>;
  createLabel: (data: { text: string }) => Promise<CollectionLabel>;
  createLabelsBulk: (data: { texts: string[] }) => Promise<CollectionLabel[]>;
  createCollectionCode: (
    collectionId: string,
    orderName: string,
    multiUse?: boolean,
    expiresAt?: Date,
  ) => Promise<{ code: string }>;
  deleteCollectionCode: (codeId: string) => Promise<void>;
  detachCollectionCodeFromClock: (
    codeId: string,
    allowOffline?: boolean,
  ) => Promise<void>;
  sendCollectionCodeEmail: (codeId: string, email: string) => Promise<void>;
  sponsorCollection: (collectionId: string) => Promise<void>;
  unsponsorCollection: (collectionId: string) => Promise<void>;
  getSponsoredCollectionCode: (
    collectionId: string,
  ) => Promise<{ code: string; redemptionCount: number } | null>;
  changeSponsoredCollectionCode: (
    collectionId: string,
    code: string,
  ) => Promise<{ code: string }>;
};

const collectionApiClient: CollectionApiClient = {
  upsertApprovedCollection: async (collectionId, collection) => {
    return baseClient.put<void>({
      url: `/admin/collections/approved/${collectionId}`,
      body: collection,
    });
  },
  upsertDraftCollection: async (collectionId, collection) => {
    return baseClient.put<void>({
      url: `/admin/collections/draft/${collectionId}`,
      body: collection,
    });
  },
  deleteCollection: async (collectionId) => {
    return baseClient.delete<void>({
      url: `/admin/collections/${collectionId}`,
    });
  },
  getCollectionById: async (collectionId) => {
    return baseClient.get<PopulatedCollection>({
      url: `/admin/collections/${collectionId}`,
    });
  },
  getApprovedCollections: async ({
    query,
    skip,
    limit,
    filter,
    sort,
    returnCreator,
  }) => {
    return baseClient.post<PopulatedApprovedCollection[]>({
      url: `/admin/collections/.search/approved-collections?query=${query}`,
      body: {
        skip,
        limit,
        filter,
        sort,
        returnCreator,
      },
    });
  },
  getReviewedCollections: (query: string, skip, limit, returnCreator) => {
    return baseClient.post<PopulatedDraftCollection[]>({
      url: `/admin/collections/.search/reviewed-collections?query=${query}`,
      body: {
        limit,
        skip,
        returnCreator,
      },
    });
  },
  getDraftCollections: async ({
    query,
    skip,
    limit,
    filter,
    sort,
    returnCreator,
  }) => {
    return baseClient.post<PopulatedDraftCollection[]>({
      url: `/admin/collections/.search/draft-collections?query=${encodeURIComponent(query)}`,
      body: { skip, limit, filter, sort, returnCreator },
    });
  },
  approveCollection: async ({
    collectionId,
    message,
    planId,
    marketplaceStatus,
  }) => {
    return baseClient.put<void>({
      url: `/admin/collections/approve/${collectionId}`,
      body: { message, planId, marketplaceStatus },
    });
  },
  publishCollectionToShopify: async (collectionId, date?) => {
    return baseClient.post<void>({
      url: `/admin/collections/publish/${collectionId}`,
      body: date ? { date } : {},
    });
  },
  createEdition: async (collectionId, creator) => {
    return baseClient.post<{ productId: string; url: string }>({
      url: `/admin/collections/${collectionId}/edition`,
      body: { creator },
    });
  },
  publishPendingCollections: async () => {
    return baseClient.post<PendingCollectionsResult>({
      url: `/admin/collections/publish-pending`,
    });
  },
  setCollectionEta: async (collectionId, eta?) => {
    return baseClient.post<void>({
      url: `/admin/collections/set-eta/${collectionId}`,
      body: { eta: eta ?? null },
    });
  },
  // A zip, so it needs a blob response; baseClient only returns parsed JSON.
  downloadCollectionAssets: async (collectionId) => {
    const res = await axios.get<Blob>(
      `${baseUrl}/admin/collections/${collectionId}/assets.zip`,
      {
        headers: { Authorization: `Bearer ${await getToken()}` },
        responseType: "blob",
      },
    );
    const disposition: string = res.headers["content-disposition"] ?? "";
    // RFC 6266: the UTF-8 form (percent-encoded) wins over the ASCII fallback.
    const utf8Name = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
    const asciiName = /filename="([^"]+)"/.exec(disposition)?.[1];
    return {
      blob: res.data,
      fileName: utf8Name
        ? decodeURIComponent(utf8Name)
        : (asciiName ?? `${collectionId}-assets.zip`),
    };
  },
  updateCollectionPlan: async (collectionId, planId) => {
    return baseClient.put<void>({
      url: `/admin/collections/${collectionId}/plan`,
      body: { planId },
    });
  },
  editApprovedCollection: async (collectionId, edit) => {
    return baseClient.put<void>({
      url: `/admin/collections/${collectionId}/edit`,
      body: edit,
    });
  },
  getCollectionCodes: async ({
    query,
    skip,
    limit,
    sort,
    filter,
    populateOrder,
  }) => {
    return baseClient.post<CollectionCode[]>({
      url: `/admin/collections/codes/.search`,
      body: {
        query,
        skip,
        limit,
        sort,
        filters: filter,
        populateOrder,
      },
    });
  },
  getCollectionOrders: async ({ query, skip, limit, sort }) => {
    return baseClient.post<CollectionOrder[]>({
      url: `/admin/collections/.search/collection-orders?query=${query}`,
      body: {
        skip,
        limit,
        sort,
      },
    });
  },
  getCollectionLabels: async ({ query, skip, limit, sort }) => {
    return baseClient.post<CollectionLabel[]>({
      url: `/admin/collections/labels/.search`,
      body: {
        query,
        skip,
        limit,
        sort,
      },
    });
  },
  updateLabel: async (labelId, text) => {
    return baseClient.put<CollectionLabel>({
      url: `/admin/collections/labels/${labelId}`,
      body: { text },
    });
  },
  deleteLabel: async (labelId) => {
    return baseClient.delete<void>({
      url: `/admin/collections/labels/${labelId}`,
    });
  },
  createLabel: async (data) => {
    return baseClient.post<CollectionLabel>({
      url: `/admin/collections/labels`,
      body: data,
    });
  },
  createLabelsBulk: async (data) => {
    return baseClient.post<CollectionLabel[]>({
      url: `/admin/collections/labels/bulk`,
      body: data,
    });
  },
  createCollectionCode: async (
    collectionId,
    orderName,
    multiUse,
    expiresAt,
  ) => {
    return baseClient.post<{ code: string }>({
      url: `/admin/collections/codes`,
      body: {
        collectionId,
        orderName,
        multiUse,
        expiresAt,
      },
    });
  },
  deleteCollectionCode: async (codeId) => {
    return baseClient.delete<void>({
      url: `/admin/collections/codes/${codeId}`,
    });
  },
  detachCollectionCodeFromClock: async (codeId, allowOffline) => {
    return baseClient.post<void>({
      url: `/admin/collections/codes/detach/${codeId}`,
      body: { allowOffline },
    });
  },
  sponsorCollection: async (collectionId) => {
    return baseClient.put<void>({
      url: `/admin/collections/${collectionId}/sponsor`,
    });
  },
  unsponsorCollection: async (collectionId) => {
    return baseClient.put<void>({
      url: `/admin/collections/${collectionId}/unsponsor`,
    });
  },
  getSponsoredCollectionCode: async (collectionId) => {
    return baseClient.get<{ code: string; redemptionCount: number } | null>({
      url: `/admin/collections/${collectionId}/sponsored-code`,
    });
  },
  changeSponsoredCollectionCode: async (collectionId, code) => {
    return baseClient.put<{ code: string }>({
      url: `/admin/collections/${collectionId}/sponsored-code`,
      body: { code },
    });
  },
  sendCollectionCodeEmail: async (codeId, email) => {
    return baseClient.post<void>({
      url: `/admin/collections/codes/send-email`,
      body: {
        codeId,
        email,
      },
    });
  },
};

export default collectionApiClient;
